import API from './apiClient';

// ─────────────────────────────────────────────────────────────
// AUTH
// ─────────────────────────────────────────────────────────────

export const apiLogin = async (email, password) => {
  const { data } = await API.post('/auth/login', { email, password });
  localStorage.setItem('goethe_token', data.token);
  return data;
};

export const apiRegister = async ({ name, email, password, level }) => {
  const { data } = await API.post('/auth/register', { name, email, password, level });
  localStorage.setItem('goethe_token', data.token);
  return data;
};

export const apiForgotPassword = async (email) => {
  const { data } = await API.post('/auth/forgot-password', { email });
  return data;
};

export const apiResetPassword = async (token, newPassword) => {
  const { data } = await API.post('/auth/reset-password', { token, newPassword });
  return data;
};

export const apiGetMe = async () => {
  const { data } = await API.get('/auth/me');
  return data;
};

export const apiChangePassword = async (currentPassword, newPassword) => {
  const { data } = await API.post('/auth/change-password', { currentPassword, newPassword });
  return data;
};

// ─────────────────────────────────────────────────────────────
// STUDENT
// ─────────────────────────────────────────────────────────────

export const apiGetDashboard = async () => {
  const { data } = await API.get('/students/dashboard');
  return data;
};

export const apiGetStudentLevels = async () => {
  const { data } = await API.get('/students/levels');
  return data;
};

export const apiGetWeakPoints = async () => {
  const { data } = await API.get('/students/weak-points');
  return data;
};

export const apiCompleteLevel = async (studentId, level) => {
  const { data } = await API.put(`/students/${studentId}/complete-level`, { level });
  return data;
};

export const apiCheckPaymentStatus = async () => {
  const { data } = await API.get('/payments/my-status');
  return data;
};

// ─────────────────────────────────────────────────────────────
// FLASHCARDS
// ─────────────────────────────────────────────────────────────

export const apiGetFlashcardTopics = async () => {
  const { data } = await API.get('/flashcards/topics');
  return data;
};

export const apiGetFlashcards = async (topicId) => {
  const { data } = await API.get(`/flashcards/topics/${topicId}/cards`);
  return data;
};

export const apiStartFlashcardSession = async (topicId) => {
  const { data } = await API.post('/flashcards/sessions', { topicId });
  return data;
};

export const apiCheckFlashcardAnswer = async (sessionId, questionId, studentAnswer) => {
  const { data } = await API.post(`/flashcards/sessions/${sessionId}/answer`, {
    questionId,
    studentAnswer,
  });
  return data;
};

export const apiCompleteFlashcardSession = async (sessionId, results) => {
  const { data } = await API.post(`/flashcards/sessions/${sessionId}/complete`, {
    results: (results || []).map(r => ({
      questionId:    r.questionId   || r.card?.id,
      correct:       r.correct,
      type:          r.type         || r.card?.type,
      questionType:  r.questionType || r.card?.questionType,
      grammarPoint:  r.grammarPoint || r.card?.grammarPoint,
      correctAnswer: r.correctAnswer,
      prompt:        r.prompt       || r.card?.prompt,
    })),
  });
  return data;
};

export const apiSendFlashcardReport = apiCompleteFlashcardSession;

export const apiGetFlashcardReport = async (studentId) => {
  const { data } = await API.get(`/flashcards/sessions/report/${studentId}`);
  return data;
};

// ─────────────────────────────────────────────────────────────
// EXAMS
// ─────────────────────────────────────────────────────────────

export const apiGetExams = async () => {
  const { data } = await API.get('/exams');
  return data;
};

export const apiGetExam = async (examId) => {
  const { data } = await API.get(`/exams/${examId}`);
  return data;
};

export const apiStartExamAttempt = async (examId) => {
  const { data } = await API.post(`/exams/${examId}/attempt`);
  return data;
};

export const apiSubmitExam = async (examId, attemptId, answers) => {
  const { data } = await API.post(`/exams/${examId}/attempt/${attemptId}/submit`, answers);
  return data;
};

export const apiGetExamAttempts = async (studentId) => {
  const { data } = await API.get(`/exams/attempts/${studentId}`);
  return data;
};

export const apiGetFullExam = async (examId) => {
  const { data } = await API.get(`/exams/${examId}/full`);
  return data;
};

export const apiListExamsAdmin = async () => {
  const { data } = await API.get('/exams/admin/list');
  return data;
};

export const apiCreateExam = async (formData) => {
  const { data } = await API.post('/exams', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const apiGetSchreibenPrompt = async (level, teil) => {
  const { data } = await API.get(`/exams/schreiben-prompt?level=${level}&teil=${teil}`);
  return data;
};

export const apiGetExamLevels = async () => {
  const { data } = await API.get('/exams/levels');
  return data;
};

export const apiStartRandomExam = async (level) => {
  const { data } = await API.get(`/exams/random/${level}`);
  return data;
};

// ─────────────────────────────────────────────────────────────
// ADMIN
// ─────────────────────────────────────────────────────────────

export const apiGetAllStudents = async () => {
  const { data } = await API.get('/admin/students');
  return data;
};

export const apiGetAdminStats = async () => {
  const { data } = await API.get('/admin/stats');
  return data;
};

export const apiGetStudentReport = async (studentId) => {
  const { data } = await API.get(`/admin/students/${studentId}/report`);
  return data;
};

export const apiUnlockLevel = async (studentId, level) => {
  const { data } = await API.put(`/admin/students/${studentId}/unlock-level`, { level });
  return data;
};

export const apiMarkLevelComplete = async (studentId, level) => {
  const { data } = await API.put(`/admin/students/${studentId}/mark-complete`, { level });
  return data;
};

export const apiGetPayments = async () => {
  const { data } = await API.get('/admin/payments');
  return data;
};

export const apiConfirmPayment = async (paymentId) => {
  const { data } = await API.put(`/admin/payments/${paymentId}/confirm`);
  return data;
};

// ─────────────────────────────────────────────────────────────
// CONTENT MANAGEMENT
// ─────────────────────────────────────────────────────────────

export const apiGetAllTopics = async () => {
  const { data } = await API.get('/content/flashcard-topics');
  return data;
};

export const apiSaveFlashcardTopic = async (topicData) => {
  const { data } = await API.post('/content/flashcard-topics', topicData);
  return data;
};

export const apiUpdateFlashcardTopic = async (topicId, updates) => {
  const { data } = await API.put(`/content/flashcard-topics/${topicId}`, updates);
  return data;
};

export const apiDeleteFlashcardTopic = async (topicId) => {
  const { data } = await API.delete(`/content/flashcard-topics/${topicId}`);
  return data;
};

export const apiGetAllExams = async () => {
  const { data } = await API.get('/content/exams');
  return data;
};

export const apiSaveExam = async (formData) => {
  const { data } = await API.post('/content/exams', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const apiUpdateExam = async (examId, formData) => {
  const { data } = await API.put(`/content/exams/${examId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
};

export const apiDeleteExam = async (examId) => {
  const { data } = await API.delete(`/content/exams/${examId}`);
  return data;
};

export const apiToggleExamPublish = async (examId, published) => {
  const { data } = await API.put(`/content/exams/${examId}/publish`, { published });
  return data;
};

export const apiGenerateGoetheExam = async (level, body = {}) => {
  const { data } = await API.post(`/exams/generate/${level}`, body);
  return data;
};

// ─────────────────────────────────────────────────────────────
// PAYMENTS
// ─────────────────────────────────────────────────────────────

export const apiInitializePayment = async ({ studentId, level, email }) => {
  const { data } = await API.post('/payments/initialize', { studentId, level, email });
  return { data };
};

export const apiVerifyPayment = async (reference) => {
  const { data } = await API.get(`/payments/verify/${reference}`);
  return data;
};

export const apiGetMyPayments = async () => {
  const { data } = await API.get('/payments/my-payments');
  return data;
};

// ─────────────────────────────────────────────────────────────
// CURRICULUM
// ─────────────────────────────────────────────────────────────

export const apiGetCurriculum = async () => {
  const { data } = await API.get('/curriculum');
  return data;
};

export const apiGetStudentCurriculum = async (studentId) => {
  const { data } = await API.get(`/curriculum/student/${studentId}`);
  return data;
};

export const apiMarkTopicComplete = async (studentId, topicId) => {
  const { data } = await API.put(`/curriculum/student/${studentId}/topic/${topicId}/complete`);
  return data;
};

// ─────────────────────────────────────────────────────────────
// PRICING
// ─────────────────────────────────────────────────────────────

export const apiGetPricing = async () => {
  const { data } = await API.get('/super-admin/pricing');
  return data;
};

export const apiUpdatePricing = async (level, body) => {
  const { data } = await API.put(`/super-admin/pricing/${level}`, body);
  return data;
};

// ─────────────────────────────────────────────────────────────
// MATERIALS
// ─────────────────────────────────────────────────────────────

export const apiGetMaterials   = () => API.get('/materials').then(r => r.data);
export const apiUploadMaterial = (formData) => API.post('/materials', formData, { headers:{'Content-Type':'multipart/form-data'} }).then(r => r.data);
export const apiDeleteMaterial = (id) => API.delete(`/materials/${id}`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// NOTES
// ─────────────────────────────────────────────────────────────

export const apiGetNotes    = () => API.get('/notes').then(r => r.data);
export const apiCreateNote  = (body) => API.post('/notes', body).then(r => r.data);
export const apiUpdateNote  = (id, body) => API.put(`/notes/${id}`, body).then(r => r.data);
export const apiDeleteNote  = (id) => API.delete(`/notes/${id}`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// VIDEOS
// ─────────────────────────────────────────────────────────────

export const apiGetVideos    = () => API.get('/videos').then(r => r.data);
export const apiCreateVideo  = (body) => API.post('/videos', body).then(r => r.data);
export const apiDeleteVideo  = (id) => API.delete(`/videos/${id}`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// MOVIES
// ─────────────────────────────────────────────────────────────

export const apiGetMovies    = () => API.get('/movies').then(r => r.data);
export const apiCreateMovie  = (body) => API.post('/movies', body).then(r => r.data);
export const apiDeleteMovie  = (id) => API.delete(`/movies/${id}`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// HOMEWORK
// ─────────────────────────────────────────────────────────────

export const apiGetHomeworkAssignments = ()       => API.get('/homework/assignments').then(r => r.data);
export const apiCreateAssignment       = (body)   => API.post('/homework/assignments', body).then(r => r.data);
export const apiDeleteAssignment       = (id)     => API.delete(`/homework/assignments/${id}`).then(r => r.data);
export const apiSubmitHomework         = (id, fd) => API.post(`/homework/assignments/${id}/submit`, fd, { headers:{'Content-Type':'multipart/form-data'} }).then(r => r.data);
export const apiGetSubmissions         = (id)     => API.get(`/homework/assignments/${id}/submissions`).then(r => r.data);
export const apiGradeSubmission        = (id, body) => API.post(`/homework/submissions/${id}/grade`, body).then(r => r.data);
export const apiGetStudentGrades       = ()       => API.get('/homework/student/grades').then(r => r.data);
export const apiDeleteHomeworkFile     = (submissionId) => API.delete(`/homework/submissions/${submissionId}/file`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// SPEAKING EXAMS
// ─────────────────────────────────────────────────────────────

export const apiGetSpeakingExams       = ()         => API.get('/speaking/exams').then(r => r.data);
export const apiCreateSpeakingExam     = (body)     => API.post('/speaking/exams', body).then(r => r.data);
export const apiDeleteSpeakingExam     = (id)       => API.delete(`/speaking/exams/${id}`).then(r => r.data);
export const apiSubmitSpeaking         = (id, body) => API.post(`/speaking/exams/${id}/submit`, body).then(r => r.data);
export const apiGetSpeakingSubmissions = (id)       => API.get(`/speaking/exams/${id}/submissions`).then(r => r.data);
export const apiGradeSpeaking          = (id, body) => API.post(`/speaking/submissions/${id}/grade`, body).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// COHORTS
// ─────────────────────────────────────────────────────────────

export const apiGetAvailableCohorts  = (level)    => API.get(`/cohorts/available/${level}`).then(r => r.data);
export const apiJoinCohort           = (id)       => API.post(`/cohorts/${id}/join`).then(r => r.data);
export const apiGetMyCohort          = ()         => API.get('/cohorts/my-cohort').then(r => r.data);
export const apiGetAllCohorts        = ()         => API.get('/cohorts').then(r => r.data);
export const apiCreateCohort         = (body)     => API.post('/cohorts', body).then(r => r.data);
export const apiUpdateCohort         = (id, body) => API.put(`/cohorts/${id}`, body).then(r => r.data);
export const apiDeleteCohort         = (id)       => API.delete(`/cohorts/${id}`).then(r => r.data);
export const apiGetCohortStudents    = (id)       => API.get(`/cohorts/${id}/students`).then(r => r.data);
export const apiAdminAddStudent      = (id, body) => API.post(`/cohorts/${id}/admin-add`, body).then(r => r.data);
export const apiRemoveStudentCohort  = (id, studentId) => API.delete(`/cohorts/${id}/students/${studentId}`).then(r => r.data);
export const apiAssignTeacherCohort  = (id, body) => API.put(`/cohorts/${id}/teacher`, body).then(r => r.data);
export const apiGetTeacherCohorts    = ()         => API.get('/cohorts/teacher').then(r => r.data);

// ─────────────────────────────────────────────────────────────
// TIMETABLE
// ─────────────────────────────────────────────────────────────

export const apiGetMyTimetable      = ()         => API.get('/timetable/my-timetable').then(r => r.data);
export const apiGetTeacherTimetable = ()         => API.get('/timetable/teacher').then(r => r.data);
export const apiGetCohortTimetable  = (id)       => API.get(`/timetable/cohort/${id}`).then(r => r.data);
export const apiCreateTimetable     = (body)     => API.post('/timetable', body).then(r => r.data);
export const apiUpdateTimetable     = (id, body) => API.put(`/timetable/${id}`, body).then(r => r.data);
export const apiDeleteTimetable     = (id)       => API.delete(`/timetable/${id}`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// ATTENDANCE
// ─────────────────────────────────────────────────────────────

export const apiGetMyAttendance     = ()         => API.get('/attendance/my-attendance').then(r => r.data);
export const apiMarkAttendance      = (body)     => API.post('/attendance/mark', body).then(r => r.data);
export const apiGetCohortAttendance = (id, date) => API.get(`/attendance/cohort/${id}/date/${date}`).then(r => r.data);
export const apiGetAttendanceReport = (id)       => API.get(`/attendance/cohort/${id}/report`).then(r => r.data);

// ─────────────────────────────────────────────────────────────
// MY TEACHER
// ─────────────────────────────────────────────────────────────

export const apiGetMyTeacher = () => API.get('/students/my-teacher').then(r => r.data);

// ─────────────────────────────────────────────────────────────
// SUPER ADMIN
// ─────────────────────────────────────────────────────────────

export const apiGetSuperAdminStats       = ()     => API.get('/super-admin/stats').then(r => r.data);
export const apiGetAllStudentsAdmin      = ()     => API.get('/super-admin/students').then(r => r.data);
export const apiAddStudentAdmin          = (body) => API.post('/super-admin/students', body).then(r => r.data);
export const apiGetAllTeachers           = ()     => API.get('/super-admin/teachers').then(r => r.data);
export const apiGetSuperAdminAssignments = ()     => API.get('/super-admin/assignments').then(r => r.data);
export const apiCreateAssignmentAdmin    = (body) => API.post('/super-admin/assignments', body).then(r => r.data);
export const apiDeleteAssignmentAdmin    = (id)   => API.delete(`/super-admin/assignments/${id}`).then(r => r.data);